import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '../../supabase'
import { Helmet } from 'react-helmet-async'
import whiteicon from '../../assets/manifestSVG.svg'

export default function Recovery() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  
  // Start as null to prevent the form from "flashing" for unauthorized users
  const [isLinkValid, setIsLinkValid] = useState(null)

useEffect(() => {
  const checkSession = async () => {
    // 1. Manually check if there is a session right now
    const { data: { session } } = await supabase.auth.getSession();
    
    // 2. If there's a session, we are good to stay on this page
    if (session) {
      setIsLinkValid(true);
      return;
    }

    // 3. If no session, set up the listener for the incoming hash
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setIsLinkValid(true);
      } else if (event === "INITIAL_SESSION" && !session) {
        // Give the hash a moment to be parsed by the SDK
        setTimeout(() => {
          if (!window.location.hash.includes('access_token')) {
            setIsLinkValid(false);
          }
        }, 1000);
      }
    });

    return () => authListener.subscription.unsubscribe();
  };

  checkSession();
}, []);

  const handleUpdatePassword = async (e) => {
    e.preventDefault()
    if (password !== confirmPassword) return setErrorMsg("Passwords do not match")
    if (password.length < 8) return setErrorMsg("Password must be at least 8 characters")

    setLoading(true)
    setErrorMsg('')

    const { error } = await supabase.auth.updateUser({ password })

    if (error) {
      setErrorMsg(error.message)
    } else {
      setSuccessMessage("Password updated! Redirecting to login...")
      setTimeout(() => navigate('/signin'), 2500)
    }
    setLoading(false)
  }

  // 1. LOADING STATE: While checking session
  if (isLinkValid === null) {
    return <div className="min-h-screen w-full bg-[#f4f4f4]" /> 
  }

  // 2. UNAUTHORIZED STATE: If someone just types the URL
  if (isLinkValid === false) {
    return (
      <div className='min-h-screen w-full bg-[#f4f4f4] flex items-center justify-center px-4'>
        <div className='max-w-[400px] w-full bg-white p-10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] text-center border border-gray-100'>
          <div className='w-14 h-14 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-5 text-2xl font-bold'>!</div>
          <h2 className='text-[#1C6065] font-bold text-xl mb-2'>Invalid or Expired Link</h2>
          <p className='text-gray-400 text-sm mb-8 leading-relaxed'>The security token for this password reset is missing or has expired for your protection.</p>
          <button 
            onClick={() => navigate('/forgot-password')} 
            className='w-full bg-[#1C6065] text-white h-[50px] rounded-xl font-bold text-sm shadow-lg hover:bg-[#164d51] transition-all'
          >
            REQUEST NEW RESET LINK
          </button>
        </div>
      </div>
    )
  }

  // 3. AUTHORIZED STATE: Show the form
  return (
    <div className='min-h-screen w-full bg-[#f4f4f4] flex items-center justify-center pb-[60px] px-4 py-6 perspective-1000'>
      <Helmet>
        <title>Set New Password | Sitemanifest</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className='w-full lg:max-w-[900px] mt-[60px] lg:mt-[90px] max-w-[450px] bg-white rounded-2xl shadow-[0_40px_80px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden flex flex-col lg:flex-row transform lg:rotate-y-[-4deg] lg:rotate-x-[2deg] transition-transform duration-700 hover:rotate-0'>
        
        {/* LEFT SIDE: Branding */}
        <div className='hidden lg:flex lg:w-2/5 bg-[#1C6065] p-10 flex-col justify-between relative overflow-hidden'>
          <div className='relative z-10'>
            <div className='flex flex-row items-center gap-[14px]'>
              <img src={whiteicon} alt="" className="w-7 h-7 brightness-0 invert" />
              <h2 className='font-staat text-white text-2xl tracking-widest uppercase'>SECURITY</h2>
            </div>
            <div className='w-8 h-[2px] bg-[#7CD998] mt-1'></div>
          </div>
          <div className='relative z-10 bg-white/10 backdrop-blur-md border border-white/10 p-6 rounded-xl shadow-xl'>
            <p className='text-white font-poppins text-sm italic opacity-90'>"Your security is our priority. Choose a strong password."</p>
          </div>
          <div className='w-8 h-1 rounded-full bg-white relative z-10'></div>
        </div>

        {/* RIGHT SIDE: The Form */}
        <div className='w-full lg:w-3/5 p-8 sm:p-10 flex flex-col justify-center bg-white'>
          <div className='mb-6'>
            <h1 className='text-2xl font-bold text-[#1C6065] tracking-tight'>Set New Password</h1>
            <p className='text-gray-400 text-[11px] mt-1 uppercase font-bold tracking-widest'>Final Step</p>
          </div>

          {(errorMsg || successMessage) && (
            <div className="mb-4 animate-message">
              {errorMsg && <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-[11px] font-bold">{errorMsg}</div>}
              {successMessage && <div className="p-3 rounded-xl bg-green-50 border border-green-100 text-[#1C6065] text-[11px] font-bold">{successMessage}</div>}
            </div>
          )}

          <form onSubmit={handleUpdatePassword} className='space-y-4'>
            <div className='space-y-1'>
              <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>New Password</label>
              <input 
                type="password" 
                placeholder="••••••••" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
                className="w-full bg-gray-50 border border-gray-100 text-sm h-[46px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" 
              />
            </div>

            <div className='space-y-1'>
              <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Confirm Password</label>
              <input 
                type="password" 
                placeholder="••••••••" 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                required 
                className="w-full bg-gray-50 border border-gray-100 text-sm h-[46px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" 
              />
            </div>

            <button 
              type="submit" 
              disabled={loading} 
              className="w-full bg-[#1C6065] h-[48px] rounded-xl font-bold text-sm text-white shadow-lg hover:bg-[#164d51] transition-all transform active:scale-[0.98]"
            >
              {loading ? 'UPDATING...' : 'UPDATE PASSWORD'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}